<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ColumnAutomationEditor from './ColumnAutomationEditor.vue';
import { ACTION_CATALOG, actionLabelKey, scheduleLabel } from './catalog';

const props = defineProps({
  pipelineId: { type: [Number, String], required: true },
  column: { type: Object, required: true },
});
const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

// modo do painel: 'list' | 'catalog' | 'edit'
const mode = ref('list');
const editingAutomation = ref(null);
const newActionType = ref('add_tag');

const automations = computed(() => {
  const raw = [...(store.getters['kanbanAutomations/getAutomations'] ?? [])];
  return raw.sort((a, b) => {
    const delayOf = auto => {
      const s = auto.trigger_config?.schedule;
      if (!s || !Number(s.value)) return 0;
      const v = Number(s.value);
      if (s.unit === 'hours') return v * 60;
      if (s.unit === 'days') return v * 1440;
      if (s.unit === 'months') return v * 1440 * 30;
      if (s.unit === 'years') return v * 1440 * 365;
      return v; // minutes
    };
    const timeDiff = delayOf(a) - delayOf(b);
    if (timeDiff !== 0) return timeDiff;
    const typeA = a.actions?.[0]?.type || a.name || '';
    const typeB = b.actions?.[0]?.type || b.name || '';
    if (typeA !== typeB) return typeA.localeCompare(typeB);
    return a.id - b.id;
  });
});

// rótulos traduzidos em JS para evitar chaves i18n dinâmicas no template
const catalogGroups = computed(() =>
  ACTION_CATALOG.map(group => ({
    category: group.category,
    label: t(`KANBAN.AUTOMATIONS.CATEGORIES.${group.category}`),
    actions: group.actions.map(action => ({
      ...action,
      label: t(actionLabelKey(action.type)),
    })),
  }))
);

async function reload() {
  await store.dispatch('kanbanAutomations/fetch', {
    pipelineId: props.pipelineId,
    columnId: props.column.id,
  });
}

onMounted(reload);

// lista [{ prefix, type }] para automações legadas salvas sem type no actions
// usa startsWith para tolerar nomes customizados como "Mover card para Apresentação"
const TYPE_PREFIXES = ACTION_CATALOG.flatMap(g =>
  g.actions.map(a => ({
    prefix: t(actionLabelKey(a.type)).toLowerCase(),
    type: a.type,
  }))
);

function actionType(automation) {
  const fromActions = automation.actions?.[0]?.type;
  if (fromActions) return fromActions;
  const nameLower = automation.name?.toLowerCase() || '';
  const match = TYPE_PREFIXES.find(({ prefix }) =>
    nameLower.startsWith(prefix)
  );
  return match?.type || '';
}

function titleFor(automation) {
  return automation.name || t(actionLabelKey(automation.actions?.[0]?.type));
}

const editingNameId = ref(null);
const editingNameValue = ref('');
// refs de elementos DOM não precisam ser reativas — objeto plano evita
// problemas de timing do proxy reativo dentro de function refs em v-for.
const nameInputRefs = {};

function setNameInputRef(automationId, el) {
  if (el) nameInputRefs[automationId] = el;
}

function startEditingName(automation, event) {
  event.stopPropagation();
  editingNameId.value = automation.id;
  editingNameValue.value = titleFor(automation);
  nextTick(() => nameInputRefs[automation.id]?.select());
}

async function commitName(automation) {
  const name = editingNameValue.value.trim() || titleFor(automation);
  editingNameId.value = null;
  if (name === titleFor(automation)) return;
  await store.dispatch('kanbanAutomations/update', {
    id: automation.id,
    name,
  });
  await reload();
}

function cancelName() {
  editingNameId.value = null;
}

function scheduleTextFor(automation) {
  const label = scheduleLabel(automation.trigger_config?.schedule);
  return label
    ? t('KANBAN.AUTOMATIONS.PANEL.EXECUTE_IN', { label })
    : t('KANBAN.AUTOMATIONS.PANEL.EXECUTE_IMMEDIATELY');
}

async function toggleActive(automation) {
  await store.dispatch('kanbanAutomations/update', {
    id: automation.id,
    active: !automation.active,
  });
}

function openCatalog() {
  mode.value = 'catalog';
}

function pickAction(type) {
  newActionType.value = type;
  editingAutomation.value = null;
  mode.value = 'edit';
}

function editAutomation(automation) {
  editingAutomation.value = automation;
  mode.value = 'edit';
}

async function onSaved() {
  await reload();
  mode.value = 'list';
  editingAutomation.value = null;
}

async function onDelete() {
  if (!editingAutomation.value?.id) return;
  await store.dispatch('kanbanAutomations/delete', editingAutomation.value.id);
  await reload();
  mode.value = 'list';
  editingAutomation.value = null;
}

function backToList() {
  mode.value = 'list';
  editingAutomation.value = null;
}
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 bg-black/40" @click.self="emit('close')">
      <div
        class="fixed right-0 top-0 h-full w-[420px] max-w-full bg-n-background border-l border-n-weak shadow-xl flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <div class="flex items-center gap-2 min-w-0">
            <button
              v-if="mode !== 'list'"
              class="text-n-slate-10 hover:text-n-slate-12"
              @click="backToList"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <span class="i-lucide-bot size-4 text-n-slate-11 flex-shrink-0" />
            <h3 class="text-sm font-semibold text-n-slate-12 truncate">
              {{ column.name }}
            </h3>
          </div>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-4">
          <!-- LIST -->
          <template v-if="mode === 'list'">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm font-semibold text-n-slate-12">
                {{
                  $t('KANBAN.AUTOMATIONS.PANEL.COUNT', {
                    count: automations.length,
                  })
                }}
              </span>
              <button
                class="flex items-center justify-center size-7 rounded-lg bg-n-alpha-2 text-n-brand hover:brightness-110"
                :aria-label="$t('KANBAN.AUTOMATIONS.PANEL.ADD')"
                @click="openCatalog"
              >
                <span class="i-lucide-plus size-4" />
              </button>
            </div>

            <p
              v-if="!automations.length"
              class="text-sm text-n-slate-10 italic mt-4"
            >
              {{ $t('KANBAN.AUTOMATIONS.PANEL.EMPTY') }}
            </p>

            <ul class="flex flex-col gap-2 list-none m-0 p-0">
              <li
                v-for="automation in automations"
                :key="automation.id"
                class="rounded-lg border border-n-weak bg-[#FEFEFE] dark:!bg-[#141517] cursor-pointer hover:bg-n-alpha-1 transition-colors"
                @click="editAutomation(automation)"
              >
                <div class="flex items-center gap-2 px-3 py-2.5">
                  <!-- ícones estáticos por tipo — necessário para detecção pelo UnoCSS -->
                  <template v-if="actionType(automation) === 'add_tag'">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 26 26"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="flex-shrink-0 text-n-slate-11"
                    >
                      <g transform="scale(0.88) translate(1, 3)">
                        <path
                          d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
                        />
                        <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                      </g>
                      <line x1="19" y1="0.5" x2="19" y2="6.5" />
                      <line x1="16" y1="3.5" x2="22" y2="3.5" />
                    </svg>
                  </template>
                  <template v-else-if="actionType(automation) === 'remove_tag'">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 26 26"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="flex-shrink-0 text-n-slate-11"
                    >
                      <g transform="scale(0.88) translate(1, 3)">
                        <path
                          d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
                        />
                        <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                      </g>
                      <line x1="16" y1="3.5" x2="22" y2="3.5" />
                    </svg>
                  </template>
                  <span
                    v-else-if="actionType(automation) === 'move_card'"
                    class="i-lucide-arrow-right-left size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'change_status'"
                    class="i-lucide-flag size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'create_card'"
                    class="i-lucide-layout-dashboard size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'duplicate_card'"
                    class="i-lucide-copy size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'create_task'"
                    class="i-lucide-check-square size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'change_assignee'"
                    class="i-lucide-user size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'add_watchers'"
                    class="i-lucide-users size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'send_message'"
                    class="i-lucide-message-square size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'trigger_webhook'"
                    class="i-lucide-zap size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else-if="actionType(automation) === 'apply_task_group'"
                    class="i-lucide-list-checks size-4 flex-shrink-0 text-n-slate-11"
                  />
                  <span
                    v-else
                    class="i-lucide-bot size-4 flex-shrink-0 text-n-slate-11"
                  />

                  <div class="flex flex-col min-w-0 flex-1">
                    <input
                      v-if="editingNameId === automation.id"
                      :ref="el => setNameInputRef(automation.id, el)"
                      v-model="editingNameValue"
                      type="text"
                      class="w-full text-sm font-medium text-n-slate-12 !bg-transparent !outline-none !shadow-none !p-0 !m-0 !h-auto !rounded-none border-b border-n-weak"
                      @click.stop
                      @blur="commitName(automation)"
                      @keydown.enter.prevent="commitName(automation)"
                      @keydown.escape.prevent="cancelName"
                    />
                    <span
                      v-else
                      class="text-sm font-medium text-n-slate-12 truncate cursor-text hover:text-n-brand"
                      @click.stop="startEditingName(automation, $event)"
                    >
                      {{ titleFor(automation) }}
                    </span>
                    <span
                      class="flex items-center gap-1 text-xs text-n-slate-10"
                    >
                      <span class="i-lucide-clock size-3" />
                      {{ scheduleTextFor(automation) }}
                    </span>
                  </div>
                  <button
                    class="flex-shrink-0"
                    :aria-label="$t('KANBAN.AUTOMATIONS.ACTIVE')"
                    @click.stop="toggleActive(automation)"
                  >
                    <!-- eslint-disable vue/no-static-inline-styles -->
                    <span
                      class="block rounded-full transition-colors relative flex-shrink-0"
                      style="width: 26px; height: 14px"
                      :style="
                        automation.active
                          ? 'background:rgb(var(--teal-9))'
                          : 'background:rgb(var(--ruby-9))'
                      "
                    >
                      <span
                        class="absolute rounded-full bg-white transition-all"
                        style="top: 2px; width: 10px; height: 10px"
                        :style="automation.active ? 'left:14px' : 'left:2px'"
                      />
                    </span>
                    <!-- eslint-enable vue/no-static-inline-styles -->
                  </button>
                  <button
                    class="text-n-slate-10 hover:text-n-slate-12 flex-shrink-0"
                    :aria-label="$t('KANBAN.AUTOMATIONS.EDIT')"
                    @click.stop="editAutomation(automation)"
                  >
                    <span class="i-lucide-chevron-right size-4" />
                  </button>
                </div>
              </li>
            </ul>
          </template>

          <!-- CATALOG -->
          <template v-else-if="mode === 'catalog'">
            <div
              v-for="group in catalogGroups"
              :key="group.category"
              class="mb-4"
            >
              <ul class="flex flex-col gap-0.5 list-none">
                <li v-for="action in group.actions" :key="action.type">
                  <button
                    class="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
                    @click="pickAction(action.type)"
                  >
                    <template v-if="action.type === 'add_tag'">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 26 26"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="flex-shrink-0 text-n-slate-11"
                      >
                        <g transform="scale(0.88) translate(1, 3)">
                          <path
                            d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
                          />
                          <circle
                            cx="7.5"
                            cy="7.5"
                            r=".5"
                            fill="currentColor"
                          />
                        </g>
                        <line x1="19" y1="0.5" x2="19" y2="6.5" />
                        <line x1="16" y1="3.5" x2="22" y2="3.5" />
                      </svg>
                    </template>
                    <template v-else-if="action.type === 'remove_tag'">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 26 26"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="flex-shrink-0 text-n-slate-11"
                      >
                        <g transform="scale(0.88) translate(1, 3)">
                          <path
                            d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
                          />
                          <circle
                            cx="7.5"
                            cy="7.5"
                            r=".5"
                            fill="currentColor"
                          />
                        </g>
                        <line x1="16" y1="3.5" x2="22" y2="3.5" />
                      </svg>
                    </template>
                    <span
                      v-else
                      class="size-4 flex-shrink-0 text-n-slate-11"
                      :class="action.icon"
                    />
                    {{ action.label }}
                  </button>
                </li>
              </ul>
            </div>
          </template>

          <!-- EDIT -->
          <ColumnAutomationEditor
            v-else-if="mode === 'edit'"
            :pipeline-id="pipelineId"
            :column-id="column.id"
            :automation="editingAutomation"
            :initial-action-type="newActionType"
            @saved="onSaved"
            @cancel="backToList"
            @delete="onDelete"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
